/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type {
  CareerRole_MerchandisingJobCategory as CareerRoleCategory,
  CareerRole_PathwayLevel as ProficiencyLevel,
} from '__generated__/graphql-types';

import { Grid, PageGridContainer } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import CareerRoleCard from 'bundles/unified-common/components/CareerRoleCard';
import useCareerRolesCollection, {
  normalizeCareerRolesCollection,
} from 'bundles/unified-common/hooks/useCareerRolesCollection';
import CareerAcademyModule from 'bundles/unified-common/modules/CareerAcademyModule';
import type { CareerRolesCollectionQueryQuery as CareerRolesCollectionQueryType } from 'bundles/unified-common/queries/__generated__/CareerRolesCollection';
import type { CareerRolesCollectionType } from 'bundles/unified-common/types/careerRoles';
import { generateCacheKey, getChipFilters, getDropdownMenuProps } from 'bundles/unified-common/utils/pathwaysFilters';

import _t from 'i18n!nls/front-page';

const styles = {
  container: css`
    margin-top: var(--cds-spacing-600);
  `,
  wrapper: css`
    border-radius: var(--cds-border-radius-200);
    overflow: hidden;
  `,
};

const CareerAcademyModuleRenderer = () => {
  const track = useTracker();
  const chipFilters = getChipFilters();
  const dropdownMenuProps = getDropdownMenuProps();
  const [currentSelectedDropdownValue, setSelectedDropdownValue] = React.useState<ProficiencyLevel>(
    dropdownMenuProps?.defaultValue
  );
  const [currentChipFilterIndex, setCurrentChipFilterIndex] = React.useState(0);

  const viewedCombinations = React.useRef<Map<string, CareerRolesCollectionType | undefined>>(new Map());
  const combinationKey = generateCacheKey(currentSelectedDropdownValue, currentChipFilterIndex);
  const cachedContent = viewedCombinations.current.get(combinationKey);
  const skipQuery = viewedCombinations.current.has(combinationKey);

  const addToLocalCache = (data: CareerRolesCollectionQueryType) => {
    if (data.Collection !== undefined && !viewedCombinations.current.has(combinationKey)) {
      viewedCombinations.current.set(combinationKey, normalizeCareerRolesCollection(data.Collection?.queryCollection));
    }
  };

  const { data: fetchedContent, loading: isCollectionLoading } = useCareerRolesCollection({
    ssr: false,
    skip: skipQuery,
    level: currentSelectedDropdownValue,
    limit: 3,
    jobCategory:
      currentChipFilterIndex === 0 ? undefined : (chipFilters[currentChipFilterIndex].id as CareerRoleCategory),
    onCompleted: (data) => {
      addToLocalCache(data);
    },
  });

  const handleFilterChange = (index: number) => {
    setCurrentChipFilterIndex(index);
  };

  const handleDropdownChange = (value: ProficiencyLevel) => {
    setSelectedDropdownValue(value);
  };

  const trackCtaClick = () => {
    track('click_button', {
      button: {
        name: 'career_academy_cta',
        linkName: 'explore_all_roles',
        linkType: 'internal',
        linkURL: '/career-academy',
      },
    });
  };

  return (
    <PageGridContainer component="section" css={styles.container}>
      <Grid item xs={12}>
        <div css={styles.wrapper}>
          <CareerAcademyModule
            headline={_t('Choose your role')}
            description={_t('Gain the knowledge and skills you need to advance.')}
            filters={chipFilters}
            collection={cachedContent || fetchedContent}
            cta={{
              text: _t('Explore all roles'),
              target: '/career-academy',
              variant: 'primary',
              onClick: trackCtaClick,
            }}
            dropdownMenu={dropdownMenuProps}
            ProductCardComponent={CareerRoleCard}
            currentFilterIndex={currentChipFilterIndex}
            onFilterChange={handleFilterChange}
            currentDropdownValue={currentSelectedDropdownValue}
            onDropdownMenuChange={handleDropdownChange}
            layout="text2/image10"
            backgroundColor="grey"
            showRoundedCorners
            showAllEntries
            isCollectionLoading={!cachedContent && isCollectionLoading}
          />
        </div>
      </Grid>
    </PageGridContainer>
  );
};

export default CareerAcademyModuleRenderer;
